import moment from "moment";
import "moment-timezone";
import {niceUserName} from "../../../../utils/functions";
import {getTestName} from "../../../../shared/types/testTypes";
import {ISelectableStudent, IUser} from "../../../../shared/types/Users";
import {APICreateTestCustom} from "../api/createTestCustom";
import {APICreateTestTraining} from "../api/createTestsTraining";
import {APIPostCustomSubcategories} from "../../listing/api/customItem";
import {getSchoolSubjectStandardTypeId} from "../../../../shared/types/subjectTypes";
import {getSubjects} from "../../../../utils/subjects";
import {StandardFile} from "../../../../utils/testsStandards/_standardType";
import {APIPostCategories} from "../api/createTest";

export const MAX_SELECTED_STANDARDS = 4;

export const extractSelectedEntities = (entities: any[], key: string, formatter: Function) =>
    entities
        .filter((entity) => entity.checked)
        .map((entity) => formatter(entity));

export const extractClassroomData = (classrooms: any[]) =>
    classrooms.map((classroom) => ({
        classroom_id: classroom.id,
        classroom_name: classroom.name,
        classroom_grade: classroom.grade,
        start_at: moment(classroom.start_date).locale("ro").format("YYYY-MM-DD HH:mm:ss"),
        students: extractSelectedEntities(classroom.students, "students", (student: any) => ({
            id: student.id,
            first_name: student.first_name,
            last_name: student.last_name,
        })),
        teachers: extractSelectedEntities(classroom.teachers, "teachers", (teacher: any) => ({
            id: teacher.id,
            name: niceUserName(teacher.first_name, teacher.last_name),
        })),
    }));

export const areStudentsCheckedInEachClassroom = (classrooms: any) => {
    return classrooms.map((classroom: any) => {
        return {
            classroomId: classroom.id,
            classroomName: classroom.name,
            hasCheckedStudents: classroom.students.some((student: any) => student.checked),
        };
    });
};

export const areTeachersCheckedInEachClassroom = (classrooms: any) => {
    return classrooms.map((classroom: any) => {
        return {
            classroomId: classroom.id,
            classroomName: classroom.name,
            hasCheckedTeachers: classroom.teachers.some((teacher: any) => teacher.checked),
        };
    });
}

export const isNotValidClassroomCheck = (classrooms: any)  => {
    const studentChecks = areStudentsCheckedInEachClassroom(classrooms);
    const teacherChecks = areTeachersCheckedInEachClassroom(classrooms);

    return (
        studentChecks.length === 0 ||
        teacherChecks.length === 0 ||
        teacherChecks.some((el: any) => !el.hasCheckedTeachers) ||
        studentChecks.some((el: any) => !el.hasCheckedStudents)
    );
};

export const getCategoriesIds = (grade: number|string, categories: any, testType: string) => {
    let selectedCategoriesId: any[];

    if(grade <= 8 && (testType === "school" || testType === "training")) {
        selectedCategoriesId = categories
            .map((category: any) => category.sub).flat()
            .map((sub: any) => sub.competenta).flat()
            .map((competenta: any) => competenta.standarde).flat()
            .filter((item: any) => item.checked === true).map(({id}: any) => id);
    } else {
        selectedCategoriesId = categories
            .map((category: any) => category.subcategories).flat()
            .filter((item: any) => item.checked === true).map(({id}: any) => id);
    }

    return selectedCategoriesId;
}

export const findMatch = (item: any, items: any) => JSON.stringify(items).includes(`"checked":${item}`);

export const getSuccessMessage = (testType: string) => {
    const title = getTestName(testType);
    return `Sesiunile de testare generate pentru clasa selectată le-au fost transmise elevilor pe e-mail. Veți regăsi sesiunile generate în pagina ${title}, secțiunea Teste active.`;
}

const bucharestDate = moment().tz("Europe/Bucharest");
const year = bucharestDate.year();
const month = bucharestDate.month();
const day = bucharestDate.date();
const hour = bucharestDate.hour();
const minute = bucharestDate.minute();
const second = bucharestDate.second();
export const bucharestDateObject = new Date(year, month, day, hour, minute, second);

export const getDigitalLiteracyGradesIds = (grade: number) => {
    if (grade < 5) {
        return 70;
    } else if(grade >= 5 && grade <= 8) {
        return 71;
    } else return 72;
}

export const mapStudentOption = (student: IUser): ISelectableStudent => ({
    ...student,
    label: niceUserName(student.first_name, student.last_name, student.email),
    value: student.id,
    checked: true,
});

export const mapCustomCategories = (items: any[]) =>
    items.map((item) => ({
        id: item.id,
        name: item.name,
        label: item.name,
        value: String(item.id),
    }));

export const mapLiteracyCategories = (items: any[]) =>
    items.map((item) => ({
        id: item.id,
        name: item.name,
        subcategories: item.subcategories.map((subcategory: any) => ({
            ...subcategory,
            disabled: true,
            label: subcategory.name,
            value: String(subcategory.id),
            checked: true,
        })),
    }));

export const selectedStandardsCount = (categories: any[]) => {
    return categories.reduce(
        (total: number, category: any) =>
            total +
            (category.sub ?? []).reduce(
                (subTotal: number, sub: any) =>
                    subTotal +
                    (sub.competenta ?? []).reduce(
                        (competenceTotal: number, competence: any) =>
                            competenceTotal +
                            (competence.standarde ?? []).filter(
                                (standard: any) => standard.checked,
                            ).length,
                        0,
                    ),
                0,
            ),
        0,
    );
}

export const resolveSelectedGrade = (type: string, grade: { value?: string | number; gradeId?: number | string }) => {
    switch (type) {
        case "bac":
        case "evaluate":
            return null;
        case "digitalLiteracy":
            return grade.gradeId ?? null;
        case "numericLiteracy":
            return 197;
        default:
            return grade.value ?? null;
    }
};

export const resolveItemsNumber = (type: string, testDurationValue: any, itemsNumberValue: any) => {
    if (type === "school") {
        return testDurationValue === 0 ? null : testDurationValue;
    }
    return itemsNumberValue;
};

export const resolveSubcategories = (type: string, items: Array<{ subcategory_id?: number | string }>, gradeValue: any, categories: any[]) => {
    if (type === "numericLiteracy") {
        return [0];
    }
    if (type === "customTest") {
        return items.map((item) => item.subcategory_id);
    }
    return getCategoriesIds(gradeValue, categories, type);
};

export const API_BY_TYPE: Record<string, (payload: any) => Promise<any>> = {
    customTest: APICreateTestCustom,
    training: APICreateTestTraining,
};

export const fetchCategories = async (
    testType: string,
    grade: number,
    subjectValue: string,
    difficultyValue: string
) => {
    if (testType === "customTest") {
        const response = await APIPostCustomSubcategories({ grade, subject: subjectValue });
        if (!response.data?.success) throw new Error("A002");
        return mapCustomCategories(response.data.data.items);
    }

    if (testType === "school" || testType === "training") {
        const subjectStandard = getSchoolSubjectStandardTypeId(Number(subjectValue));
        const subjectStandardId =
            subjectStandard?.newStandardId ??
            getSubjects("school").find((subject) => subject.value === subjectValue)?.newStandardId;

        if (grade <= 8 && subjectStandardId !== undefined) {
            const { default: standardData }: { default: StandardFile } = await import(
                `utils/testsStandards/dis_${subjectStandardId}_cl_${grade}.json`
                );
            return standardData.strand;
        }

        const response = await APIPostCategories({ grade_id: grade, difficulty: difficultyValue });
        if (!response.data?.success) throw new Error("A002");
        return response.data.data.items;
    }

    const gradeId = testType === "digitalLiteracy" ? getDigitalLiteracyGradesIds(grade) : grade;
    const response = await APIPostCategories({ grade_id: gradeId, difficulty: difficultyValue });

    if (!response.data?.success) throw new Error("A002");

    return testType === "literacy"
        ? mapLiteracyCategories(response.data.data.items)
        : response.data.data.items;
};

export const updateItemAtIndex = <T>(
    items: T[] | undefined,
    index: number,
    updater: (item: T) => T,
): T[] => {
    if (!items) return [];
    return items.map((item, i) => (i === index ? updater(item) : item));
};